import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "../assets/dexton-logo-png.png";
import MagneticButton from "./ui/MagneticButton";

const primaryNav = [
  { name: "Home", to: "/" },
  { name: "Services", to: "/services" },
  { name: "Work", to: "/portfolio" },
  { name: "About", to: "/about" },
  { name: "Contact", to: "/contact" },
];

const mobileNav = primaryNav;

export default function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const lightHero = isHome && !scrolled;

  function navLinkClass({ isActive }) {
    const base =
      "relative text-sm font-medium transition-colors after:absolute after:-bottom-1 after:left-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-primary-600 after:transition-transform after:duration-300 hover:after:scale-x-100";
    if (lightHero) {
      return `${base} ${isActive ? "text-primary-700 after:scale-x-100" : "text-neutral-800 hover:text-primary-700"}`;
    }
    return `${base} ${isActive ? "text-primary-700 after:scale-x-100" : "text-neutral-600 hover:text-neutral-950"}`;
  }

  return (
    <header>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[70] focus:rounded-lg focus:bg-neutral-900 focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to main content
      </a>

      <nav
        className={`fixed inset-x-0 top-0 z-[60] transition-all duration-500 ${
          scrolled
            ? "border-b border-neutral-200/80 bg-white/90 shadow-sm backdrop-blur-xl"
            : isHome
              ? "border-b border-transparent bg-transparent"
              : "border-b border-neutral-200/60 bg-white/95 backdrop-blur-md"
        }`}
        aria-label="Primary navigation"
      >
        <div className="mx-auto flex h-[4.25rem] max-w-[90rem] items-center justify-between gap-4 px-4 sm:px-6 lg:px-10 xl:px-16">
          <Link to="/" className="flex items-center gap-2.5 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
            <img src={Logo} alt="" className="h-9 w-9 lg:h-10 lg:w-10" width={40} height={40} aria-hidden />
            <span className={`font-display text-lg font-bold tracking-tight ${lightHero ? "text-neutral-950" : "text-neutral-950"}`}>
              Dexton <span className="hidden xs:inline">Technology</span>
            </span>
          </Link>

          <div className="hidden items-center gap-8 md:flex">
            {primaryNav.map((item) => (
              <NavLink key={item.to} to={item.to} className={navLinkClass} end={item.to === "/"}>
                {item.name}
              </NavLink>
            ))}
          </div>

          <div className="hidden md:block">
            <MagneticButton to="/get-proposal" className="!px-5 !py-2.5 !text-sm">
              Start a Project
            </MagneticButton>
          </div>

          <button
            type="button"
            className="rounded-lg p-2.5 text-neutral-900 md:hidden"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {menuOpen ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[65] bg-neutral-950 md:hidden"
          >
            <div className="flex h-full flex-col justify-between px-6 pb-10 pt-24">
              <ul className="space-y-2">
                {mobileNav.map((item, i) => (
                  <motion.li
                    key={item.to}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.05, duration: 0.35 }}
                  >
                    <NavLink
                      to={item.to}
                      className="flex items-center gap-4 border-b border-white/10 py-5 font-display text-3xl font-semibold text-white"
                      end={item.to === "/"}
                    >
                      <span className="font-mono text-xs text-primary-300">0{i + 1}</span>
                      {item.name}
                    </NavLink>
                  </motion.li>
                ))}
              </ul>
              <Link
                to="/get-proposal"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-600 px-8 py-4 text-center font-semibold text-white"
              >
                Start a Project
                <ArrowUpRight className="h-5 w-5" />
              </Link>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
}
